import React, { useEffect, useState } from "react";
import InvestigatorContainer from "../../../components/contsiners/InvestigatorContainer";
import CAMHeader from "../../../components/Header/CAMHeader";
import CAMSubHeader from "../../../components/Header/CAMSubHeader";
import CAMDrawer from "../../../components/LeftSideBar/CAMDrawer";
import { action } from "../../../constants/action";
import FiberNewOutlinedIcon from "@mui/icons-material/FiberNewOutlined";

import "./dataValidation.scss";

import {
  Box,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Tooltip,
  InputBase,
  Button,
  TextField,
  Tab,
  Chip,
  Stack,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import DeleteTwoToneIcon from "@mui/icons-material/DeleteTwoTone";

import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import * as acts from "../../../redux/validation/actions";

import TablePagination from "@mui/material/TablePagination";
import CustomizedSnackbars from "../../../components/Snackbars/CustomizedSnackbars";
import DashboardTableSkaleton from "../../../components/Skeletons/DashboardTableSkaleton";
import ProgressBar from "../../../components/ProgressBar";
import ActionBar from "../../../components/Actionbar/ActionBars";
import moment from "moment";
import { updateType } from "../../../redux/validation/actions";
import Label from "../../../components/Label/Label";
import usePermissions from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../config/permissions";

/**
 * ValidateButton Component
 *
 * Conditionally renders the Validate button based on:
 * 1. User having "reviewer" sub-role (NOT "viewer")
 * 2. Batch status being "Draft" (not already approved)
 *
 * Only users with "reviewer" sub-role can see and use the Validate button.
 * Users with "viewer" sub-role cannot see this button.
 */
const ValidateButton = ({ batch, project, onClickValidate }) => {
  const { hasReviewerSubRole, hasViewerSubRole, canValidate } =
    usePermissions();

  const batchStatus = batch.validation_review_status
    ?.replace(/_/g, " ")
    .toLowerCase();

  const canValidateBatch = canValidate(batchStatus);

  // Hide button only if user is neither Reviewer nor Viewer
  if (!hasReviewerSubRole && !hasViewerSubRole) {
    return null;
  }

  const isApprovedOrRejected =
    batch.validation_review_status === "approved" ||
    batch.validation_review_status === "rejected";

  const isViewer = hasViewerSubRole && !hasReviewerSubRole;

  return (
    <span>
      <Button
        style={{ minWidth: "100px" }}
        size="small"
        variant="contained"
        onClick={() =>
          onClickValidate(
            project?.project_id,
            batch?.batch_id,
            project.project_name,
            batch.batch_name,
            isViewer || isApprovedOrRejected ? "viewOnly" : "validate",
          )
        }
      >
        {isViewer || isApprovedOrRejected ? "View" : "Validate"}
      </Button>
    </span>
  );
};

const DataValidationList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { hasReviewerSubRole, hasViewerSubRole } = usePermissions();

  const apiStatus = useSelector((state) => state?.validation?.apiStatus);
  const apiStatusData = useSelector(
    (state) => state?.validation?.apiStatusData,
  );

  const batch_details = useSelector((state) => state?.validation?.batchDetails);
  const type = useSelector((state) => state?.validation?.type);

  const [openRows, setOpenRows] = useState({});
  // const [pageCount, setPageCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [page, setPage] = useState(0);
  // const [projects, setProjects] = useState([]);

  const [message, setMessage] = useState("");
  const [err, setErr] = useState(false);
  const [loader, setLoader] = useState(false);
  const [success, setSuccess] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [drugType, setDrugType] = useState("");

  const DEBOUNCE_MS = 400;

  // Check if user has reviewer sub-role to determine if Action column should be shown
  // Only reviewer sub-role can see Validate button, not viewer
  const showActionColumn = hasReviewerSubRole || hasViewerSubRole;

  // Define headers - conditionally include Action column
  const header = showActionColumn
    ? [
        "Project",
        "Batch Name",
        "Status",
        // "End Date",
        "Last Updated",
        "Action",
      ]
    : [
        "Project",
        "Batch Name",
        "Status",
        //  "End Date",
        "Last Updated",
      ];

  const toggleColWidth = "50px";
  const actionColWidth = "110px"; // was effectively unbounded before — now capped
  const dataColWidths = showActionColumn
    ? ["30%", "20%", "18%", "18%"] // Unit Procedure, Batch No., Status, Last Updated (Action takes the fixed 110px above)
    : ["34%", "24%", "21%", "21%"]; // same 4 columns, redistributed since there's no Action column to reserve space for

  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    dispatch(updateType("project"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    dispatch(
      acts.getBatchDetailsList({
        type: "project",
        formattedStartDate: "",
        formattedEndDate: "",
        status: "",
        reviewFor: "validation",
        searchText: "",
        drugType: "",
        page: 1,
        page_size: rowsPerPage,
      }),
    );
  }, [dispatch]);

  // useEffect(() => {
  //   // if (projectFromState?.length > 0) {
  //   setProjects(batch_details?.batches_for_review);
  //   // }
  // }, [type]);

  // useEffect(() => {
  //   if (batch_details?.batches_for_review?.length > 0) {
  //     setProjects(batch_details.batches_for_review);
  //     setPageCount(batch_details.total_count || 0);
  //   } else {
  //     setProjects([]);
  //     setPageCount(0);
  //   }
  // }, [batch_details]);

  useEffect(() => {
    if (!apiStatus) return;

    if (apiStatus?.apiError) {
      setLoader(false);
      setMessage(apiStatus?.message || "Something went wrong");
      setErr(true);
    } else if (apiStatus?.apiSuccess) {
      setLoader(false);
      dispatch(acts.clearApiStatus());
    }
  }, [apiStatus, dispatch]);

  const toggleRow = (projectId) => {
    setOpenRows((prev) => ({
      ...prev,
      [projectId]: !prev[projectId],
    }));
  };

  const onClickValidate = (
    projectId,
    batchId,
    projectName,
    batchName,
    forValue,
  ) => {
    navigate(`/validate-data`, {
      state: { projectId, batchId, projectName, batchName, forValue },
    });
    localStorage.setItem("projectName", projectName);
    localStorage.setItem("batchName", batchName);
    localStorage.setItem("projectId", projectId);
    localStorage.setItem("batchId", batchId);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);

    dispatch(
      acts.getBatchDetailsList({
        type,
        formattedStartDate: dateRange[0],
        formattedEndDate: dateRange[1],
        status: "",
        reviewFor: "validation",
        searchText,
        drugType,
        page: newPage + 1,
        page_size: rowsPerPage,
      }),
    );
  };

  const handleChangeRowsPerPage = (event) => {
    const newVal = parseInt(event.target.value, 10) || 100;
    setRowsPerPage(newVal);
    setPage(0);

    dispatch(
      acts.getBatchDetailsList({
        type,
        formattedStartDate: dateRange[0],
        formattedEndDate: dateRange[1],
        status: "",
        reviewFor: "validation",
        searchText,
        drugType,
        page: 1,
        page_size: newVal,
      }),
    );
  };

  const onSearch = (word, type) => {
    setSearchText(word);
    // let result;
    // let filteredProjects = [];

    // if (
    //   !batch_details?.batches_for_review ||
    //   !Array.isArray(batch_details?.batches_for_review)
    // ) {
    //   setProjects([]);
    //   return;
    // }

    // if (type === "project") {
    //   if (word) {
    //     result = batch_details?.batches_for_review?.filter(function (el) {
    //       return (
    //         el?.project_name
    //           .toString()
    //           .toLowerCase()
    //           .indexOf(word.toLowerCase()) !== -1
    //       );
    //     });
    //   } else {
    //     result = batch_details?.batches_for_review;
    //   }
    // } else {
    //   if (word) {
    //     batch_details?.batches_for_review?.map((item, index) => {
    //       const filteredBatches = item?.batch_info?.filter(function (el) {
    //         return (
    //           el?.batch_name
    //             .toString()
    //             .toLowerCase()
    //             .indexOf(word.toLowerCase()) !== -1
    //         );
    //       });
    //       if (filteredBatches.length > 0) {
    //         filteredProjects.push({
    //           ...batch_details?.batches_for_review[index],
    //           batch_info: filteredBatches,
    //         });
    //       } else {
    //       }
    //     });
    //     result = filteredProjects;
    //   } else {
    //     result = batch_details?.batches_for_review;
    //   }
    // }
    // setProjects([...result]);
  };

  const filterByDate = (startDate, endDate, status) => {
    const formattedStartDate =
      startDate !== "" ? moment(startDate).format("YYYY-MM-DD") : "";
    const formattedEndDate =
      endDate !== "" ? moment(endDate).format("YYYY-MM-DD") : "";

    setDateRange([startDate, endDate]);
    dispatch(
      acts.getBatchDetailsList({
        type,
        formattedStartDate,
        formattedEndDate,
        status,
        reviewFor: "validation",
        searchText: searchText,
        drugType: drugType,
        page: page + 1,
        page_size: rowsPerPage,
      }),
    );
  };

  const clear = () => {
    setSearchText("");
    setDrugType("");
    setDateRange([null, null]);

    dispatch(updateType("project"));
    dispatch(
      acts.getBatchDetailsList({
        type: "project",
        formattedStartDate: "",
        formattedEndDate: "",
        status: "",
        reviewFor: "validation",
        searchText: "",
        drugType: "",
        page: 1,
        page_size: rowsPerPage,
      }),
    );
  };

  const projects = batch_details?.batches_for_review ?? [];

  return (
    <>
      <CAMHeader />
      <CAMDrawer />
      <InvestigatorContainer>
        {/* <CAMSubHeader text={action.DATA_VALIDATION} /> */}

        <div className="validation-wrap">
          <div style={{ paddingTop: "15px" }}>
            <ActionBar
              onSearch={(value, type) => onSearch(value, type)}
              filterByDate={filterByDate}
              clear={clear}
              // onClickViewPendingBatches={onClickViewPendingBatches}
              // list={list}
              searchText={searchText}
              drugType={drugType}
              setDrugType={setDrugType}
              setSearchText={setSearchText}
            />
          </div>

          <Box className="validation-table-wrap" style={{ paddingTop: "10px" }}>
            {apiStatusData?.loader ? (
              <DashboardTableSkaleton />
            ) : (
              <TableContainer className="validation-container">
                <Table
                  stickyHeader
                  className="validation-info-table"
                  sx={{ tableLayout: "fixed" }}
                >
                  <colgroup>
                    <col style={{ width: toggleColWidth }} />
                    {dataColWidths.map((w, idx) => (
                      <col key={`ol-${idx}`} style={{ width: w }} />
                    ))}
                    
                    {showActionColumn && (
                      <col style={{ width: actionColWidth }} />
                    )}
                  </colgroup>

                  <TableHead>
                    <TableRow className="validation-table-row">
                      <TableCell />
                      {header.map((col) => (
                        <TableCell
                          key={col}
                          align={col === "Action" ? "center" : "left"}
                        >
                          {col}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {projects?.length > 0 ? (
                      projects?.map((project, i) => {
                        const isOpen = openRows[project?.project_id];

                        return (
                          <React.Fragment
                            key={
                              project?.project_id ?? project?.project_name ?? i
                            }
                          >
                            {/* Project Row */}
                            <TableRow style={{ height: "50px" }}>
                              <TableCell>
                                <IconButton
                                  size="small"
                                  onClick={() => toggleRow(project?.project_id)}
                                >
                                  {isOpen ? (
                                    <KeyboardArrowUpIcon />
                                  ) : (
                                    <KeyboardArrowDownIcon />
                                  )}
                                </IconButton>
                              </TableCell>
                              <TableCell
                                sx={{
                                  wordBreak: "break-all",
                                  display: "flex",
                                  flexDirection: "row",
                                  marginLeft:
                                    project?.new_batch === true
                                      ? "-25px"
                                      : "0px",
                                }}
                              >
                                {project?.new_batch === true && (
                                  <FiberNewOutlinedIcon className="new-icon" />
                                )}
                                <div
                                  className="p-name"
                                  style={{
                                    marginLeft:
                                      project?.new_batch === true
                                        ? "05px"
                                        : "0px",
                                  }}
                                >
                                  <div>{project.project_name} </div>

                                  <Stack
                                    direction="row"
                                    className="drugType-wrap"
                                  >
                                    <Chip
                                      label={project.drug_type}
                                      className="drugType"
                                    />

                                    <Label
                                      fontSize={12}
                                      fontWeight={500}
                                      text={`${
                                        project.project_type === "both"
                                          ? "QC & AE"
                                          : project.project_type ===
                                              "parameter_extraction"
                                            ? "AE"
                                            : project.project_type ===
                                                "qc_extraction"
                                              ? "QC"
                                              : ""
                                      }`}
                                      padding={"0px 0px 0px 5px"}
                                    />
                                  </Stack>
                                </div>
                              </TableCell>
                              <TableCell />
                              <TableCell />
                              <TableCell>{project.updated_on}</TableCell>
                              {showActionColumn && <TableCell />}
                            </TableRow>

                            {/* Batch Rows */}
                            {isOpen &&
                              Array.isArray(project?.batch_info) &&
                              project.batch_info.map((batch) => (
                                <TableRow
                                  className="hidden-row"
                                  key={`batch-${batch?.batch_id}`}
                                >
                                  <TableCell />
                                  <TableCell className="float-right">
                                    {batch?.validation_review_status ===
                                      "assigned" && (
                                      <FiberNewOutlinedIcon className="new-icon" />
                                    )}
                                  </TableCell>
                                  <TableCell>{batch.batch_name}</TableCell>
                                  <TableCell>
                                    {/* {batch.validation_review_status ===
                                      "approved"
                                        ? "Approved"
                                        : "Draft"} */}

                                    <Label
                                      text={batch.validation_review_status?.replace(
                                        /_/g,
                                        " ",
                                      )}
                                      fontWeight={500}
                                    />
                                  </TableCell>
                                  {/* <TableCell>
                                    {batch.validation_review_end_date}
                                  </TableCell> */}
                                  <TableCell>{batch.updated_on}</TableCell>
                                  {showActionColumn && (
                                    <TableCell align="center">
                                      <ValidateButton
                                        batch={batch}
                                        project={project}
                                        onClickValidate={onClickValidate}
                                      />
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                          </React.Fragment>
                        );
                      })
                    ) : (
                      <TableRow>
                        <TableCell colSpan={header.length + 1} align="center">
                          No records found
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            <Grid className="pagination-wrap">
              <TablePagination
                component="div"
                rowsPerPageOptions={[100, 200, 300, 500]}
                count={parseInt(batch_details?.total_count) || 0}
                page={parseInt(page)}
                onPageChange={handlePageChange}
                rowsPerPage={parseInt(rowsPerPage)}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </Grid>
          </Box>

          {/* {loader && <ProgressBar />} */}

          {success && (
            <CustomizedSnackbars
              severity="success"
              state={success}
              text={message}
              handleClose={() => setSuccess(false)}
            />
          )}

          {err && (
            <CustomizedSnackbars
              severity="error"
              state={err}
              text={message}
              handleClose={() => setErr(false)}
            />
          )}
        </div>
      </InvestigatorContainer>
    </>
  );
};

export default DataValidationList;
